import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Plus,
  Search,
  Filter,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Upload,
  FileText,
  Video,
  Image,
  Link as LinkIcon,
  BookOpen,
  Clock,
  Calendar,
  User,
  Tag,
  ChevronDown,
  ChevronUp,
  Save,
  X,
  AlertTriangle,
} from "lucide-react";
import { contentService } from "../../services/elearning/contentService";
import { uploadService } from "../../services/uploadService";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";

// Local type definitions
interface ContentItem {
  _id: string;
  title: string;
  description: string;
  type: "video" | "document" | "image" | "link" | "text";
  status: "draft" | "published" | "archived";
  moduleId?: string;
  courseId: string;
  author: string;
  createdAt: string;
  updatedAt: string;
  estimatedDuration: number;
  tags: string[];
  mediaUrl?: string;
  content?: string;
  externalUrl?: string;
  visibility: "public" | "private" | "restricted";
}

interface ContentFormData {
  title: string;
  description: string;
  type: "video" | "document" | "image" | "link" | "text";
  content?: string;
  mediaUrl?: string;
  externalUrl?: string;
  estimatedDuration: number;
  tags: string[];
  visibility: "public" | "private" | "restricted";
  moduleId?: string;
  files?: File[];
  materials?: MaterialItem[];
}

interface MaterialItem {
  id: string;
  name: string;
  type: "file" | "link";
  url?: string;
  file?: File;
  description?: string;
  size?: number;
  mimeType?: string;
  createdAt?: Date;
}

interface ContentFilters {
  search: string;
  type: string;
  status: string;
  author: string;
  moduleId: string;
}

const ContentForm: React.FC<{
  content?: ContentItem;
  onSave: (data: ContentFormData) => void;
  onCancel: () => void;
  isOpen: boolean;
}> = ({ content, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState<ContentFormData>({
    title: "",
    description: "",
    type: "text",
    content: "",
    mediaUrl: "",
    externalUrl: "",
    estimatedDuration: 10,
    tags: [],
    visibility: "public",
    moduleId: "",
    files: [],
    materials: [],
  });

  // Load content data when editing
  useEffect(() => {
    if (content) {
      setFormData({
        title: content.title || "",
        description: content.description || "",
        type: content.type || "text",
        content: content.content || "",
        mediaUrl: content.mediaUrl || "",
        externalUrl: content.externalUrl || "",
        estimatedDuration: content.estimatedDuration || 10,
        tags: content.tags || [],
        visibility: content.visibility || "public",
        moduleId: content.moduleId || "",
        files: [],
        materials: [],
      });
    } else {
      // Reset form for new content
      setFormData({
        title: "",
        description: "",
        type: "text",
        content: "",
        mediaUrl: "",
        externalUrl: "",
        estimatedDuration: 10,
        tags: [],
        visibility: "public",
        moduleId: "",
        files: [],
        materials: [],
      });
    }
  }, [content]);

  const [tagInput, setTagInput] = useState("");
  const [materialInput, setMaterialInput] = useState({
    name: "",
    description: "",
    type: "file" as "file" | "link",
    url: "",
  });

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  const addMaterial = () => {
    if (materialInput.name.trim()) {
      const newMaterial: MaterialItem = {
        id: Date.now().toString(),
        name: materialInput.name.trim(),
        type: materialInput.type,
        description: materialInput.description.trim(),
        ...(materialInput.type === "link" && { url: materialInput.url }),
      };

      setFormData((prev) => ({
        ...prev,
        materials: [...(prev.materials || []), newMaterial],
      }));

      setMaterialInput({
        name: "",
        description: "",
        type: "file",
        url: "",
      });
    }
  };

  const removeMaterial = (materialId: string) => {
    setFormData((prev) => ({
      ...prev,
      materials: prev.materials?.filter((m) => m.id !== materialId) || [],
    }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // File Management Functions
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    const errors: string[] = [];

    selectedFiles.forEach((file) => {
      // Check file type
      if (!uploadService.isFileTypeAllowed(file)) {
        errors.push(
          `${file.name}: Invalid file type. Only PDF and image files are allowed.`
        );
        return;
      }

      // Check file size (50MB limit)
      if (!uploadService.isFileSizeAllowed(file)) {
        errors.push(
          `${
            file.name
          }: File too large. Maximum size is 50MB. Current size: ${uploadService.getFileSize(
            file.size
          )}`
        );
        return;
      }

      validFiles.push(file);
    });

    // Show errors if any
    if (errors.length > 0) {
      alert(errors.join("\n"));
    }

    // Add valid files
    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...validFiles],
      }));
    }

    // Clear the input
    e.target.value = "";
  };

  const removeFile = (fileIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files?.filter((_, index) => index !== fileIndex) || [],
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {content ? "Edit Content" : "Create New Content"}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter content title"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter content description"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Type *
              </label>
              <select
                required
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: e.target.value as any,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="text">Text Content</option>
                <option value="video">Video</option>
                <option value="document">Document</option>
                <option value="image">Image</option>
                <option value="link">External Link</option>
              </select>
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visibility
              </label>
              <select
                value={formData.visibility}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    visibility: e.target.value as any,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="restricted">Restricted</option>
              </select>
            </div>

            {/* Estimated Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Duration (minutes)
              </label>
              <input
                type="number"
                min="1"
                value={formData.estimatedDuration || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = value === "" ? "" : parseInt(value) || 10;
                  setFormData((prev) => ({
                    ...prev,
                    estimatedDuration: numValue,
                  }));
                }}
                placeholder="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Module ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Module (Optional)
              </label>
              <input
                type="text"
                value={formData.moduleId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, moduleId: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Module ID"
              />
            </div>
          </div>

          {/* Content-specific fields */}
          {formData.type === "video" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video URL
              </label>
              <input
                type="url"
                value={formData.mediaUrl}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mediaUrl: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com/video.mp4"
              />
            </div>
          )}

          {formData.type === "image" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image URL
              </label>
              <input
                type="url"
                value={formData.mediaUrl}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mediaUrl: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          )}

          {formData.type === "link" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                External URL
              </label>
              <input
                type="url"
                value={formData.externalUrl}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    externalUrl: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://external-resource.com"
              />
            </div>
          )}

          {(formData.type === "text" || formData.type === "document") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, content: e.target.value }))
                }
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your content here (HTML supported)"
              />
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add a tag"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg hover:bg-gray-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* File Uploads */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Files
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,image/*"
              />
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500 mb-4">
                PDF and Image files only (Max 50MB each)
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Choose Files
              </button>
            </div>

            {/* Display selected files */}
            {formData.files && formData.files.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Selected Files ({formData.files.length})
                </h4>
                <div className="space-y-2">
                  {formData.files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Additional Materials */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Materials
            </label>

            {/* Add Material Form */}
            <div className="border border-gray-300 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <input
                    type="text"
                    value={materialInput.name}
                    onChange={(e) =>
                      setMaterialInput((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Material name"
                  />
                </div>
                <div>
                  <select
                    value={materialInput.type}
                    onChange={(e) =>
                      setMaterialInput((prev) => ({
                        ...prev,
                        type: e.target.value as "file" | "link",
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="file">File Upload</option>
                    <option value="link">External Link</option>
                  </select>
                </div>
              </div>

              {materialInput.type === "link" && (
                <div className="mb-3">
                  <input
                    type="url"
                    value={materialInput.url}
                    onChange={(e) =>
                      setMaterialInput((prev) => ({
                        ...prev,
                        url: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/resource"
                  />
                </div>
              )}

              <div className="mb-3">
                <textarea
                  value={materialInput.description}
                  onChange={(e) =>
                    setMaterialInput((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Material description (optional)"
                />
              </div>

              <button
                type="button"
                onClick={addMaterial}
                disabled={!materialInput.name.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Add Material
              </button>
            </div>

            {/* Display added materials */}
            {formData.materials && formData.materials.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Added Materials ({formData.materials.length})
                </h4>
                <div className="space-y-2">
                  {formData.materials.map((material) => (
                    <div
                      key={material.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        {material.type === "link" ? (
                          <LinkIcon className="w-5 h-5 text-blue-400 mr-3" />
                        ) : (
                          <FileText className="w-5 h-5 text-gray-400 mr-3" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {material.name}
                          </p>
                          {material.description && (
                            <p className="text-xs text-gray-500">
                              {material.description}
                            </p>
                          )}
                          {material.type === "link" && material.url && (
                            <p className="text-xs text-blue-600 truncate">
                              {material.url}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMaterial(material.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="w-4 h-4 inline mr-2" />
              {content ? "Update Content" : "Create Content"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ContentManagement: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const { courseId } = useParams<{ courseId: string }>();

  const [contents, setContents] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState<
    ContentItem | undefined
  >();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "cards">("cards");
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<ContentItem | null>(
    null
  );

  const [filters, setFilters] = useState<ContentFilters>({
    search: "",
    type: "",
    status: "",
    author: "",
    moduleId: "",
  });

  useEffect(() => {
    fetchContents();
  }, [courseId, filters]);

  const fetchContents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch real content from API
      const response = await contentService.getContentList({
        courseId: courseId,
        ...filters,
      });

      // The API interceptor extracts response.data, so we get the backend response object directly
      if (response && response.success && response.data) {
        setContents(Array.isArray(response.data) ? response.data : []);
      } else {
        // Even if no content, it's not an error - just empty
        setContents([]);
      }
    } catch (err) {
      console.error("Full error object:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch contents");
      console.error("Error fetching contents:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async (formData: ContentFormData) => {
    try {
      setLoading(true);

      // First, upload any files to Cloudflare R2
      let uploadedMaterials = [...(formData.materials || [])];

      if (formData.files && formData.files.length > 0) {
        for (const file of formData.files) {
          try {
            const uploadResult = await uploadService.uploadFile(file);

            if (uploadResult.success && uploadResult.data) {
              // Add uploaded file to materials
              uploadedMaterials.push({
                id: uploadResult.data.filename,
                name: uploadResult.data.filename,
                type: "file" as const,
                description: `Uploaded file: ${file.name}`,
                url: uploadResult.data.url,
                size: uploadResult.data.size,
                mimeType: uploadResult.data.type,
                createdAt: new Date(),
              });
            } else {
              throw new Error(uploadResult.message || "Upload failed");
            }
          } catch (uploadError) {
            console.error("File upload error:", uploadError);
            const errorMessage =
              uploadError instanceof Error
                ? uploadError.message
                : "Unknown upload error";
            toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
            return; // Stop the save process if upload fails
          }
        }
      }

      if (editingContent) {
        // Update existing content
        const updateData: any = {
          title: formData.title,
          description: formData.description,
          type: formData.type, // Send type instead of contentType to match backend validation
          content: formData.content || "", // Send content as string, not object
          mediaUrl: formData.mediaUrl || "", // Include mediaUrl for videos/images
          externalUrl: formData.externalUrl || "", // Include externalUrl for links
          estimatedDuration: Number(formData.estimatedDuration) || 0,
          tags: formData.tags || [],
          visibility: formData.visibility || "public",
          courseId: courseId, // Add courseId for updates too
          moduleId: formData.moduleId || null, // Handle empty moduleId
          materials: uploadedMaterials,
        };

        const response = await contentService.updateContent(
          editingContent._id,
          updateData,
          courseId
        );

        if (response.success && response.data) {
          const updatedContent: ContentItem = {
            _id: response.data._id,
            title: response.data.title,
            description: response.data.description,
            type: response.data.type as ContentItem["type"],
            content: response.data.content || "",
            mediaUrl: response.data.mediaUrl || "",
            externalUrl: response.data.externalUrl || "",
            estimatedDuration: response.data.estimatedDuration,
            tags: response.data.tags,
            visibility: response.data.visibility,
            moduleId: response.data.moduleId,
            courseId: response.data.courseId,
            author: response.data.author || "Unknown",
            status:
              response.data.status === "deleted"
                ? "archived"
                : (response.data.status as ContentItem["status"]),
            createdAt:
              typeof response.data.createdAt === "string"
                ? response.data.createdAt
                : new Date(response.data.createdAt).toISOString(),
            updatedAt:
              typeof response.data.updatedAt === "string"
                ? response.data.updatedAt
                : new Date(response.data.updatedAt).toISOString(),
          };
          setContents((prev) =>
            prev.map((c) => (c._id === editingContent._id ? updatedContent : c))
          );
        } else {
          setError("Failed to update content");
        }
      } else {
        // For e-learning content, courseId is optional since it's standalone content
        // We can create content without being tied to a specific physical course

        // Create new content with API call
        const contentFormData: any = {
          title: formData.title,
          description: formData.description,
          type: formData.type, // Send type instead of contentType to match backend validation
          content: formData.content || "", // Send content as string, not object
          mediaUrl: formData.mediaUrl || "", // Include mediaUrl for videos/images
          externalUrl: formData.externalUrl || "", // Include externalUrl for links
          estimatedDuration: Math.floor(formData.estimatedDuration) || 1, // Backend requires positive integer
          tags: formData.tags || [],
          visibility: formData.visibility || "public",
          materials: uploadedMaterials,
        };

        // Only add courseId if it's available (for course-specific content)
        if (courseId) {
          contentFormData.courseId = courseId;
        }

        // Only add moduleId if it's not empty
        if (formData.moduleId && formData.moduleId.trim() !== "") {
          contentFormData.moduleId = formData.moduleId;
        }

        const response = await contentService.createContent(
          contentFormData,
          courseId
        );

        if (response.success && response.data) {
          // Add the new content to the list
          const newContent: ContentItem = {
            _id: response.data._id,
            title: response.data.title,
            description: response.data.description,
            type: response.data.type as ContentItem["type"],
            content: response.data.content || "",
            mediaUrl: response.data.mediaUrl || "",
            externalUrl: response.data.externalUrl || "",
            estimatedDuration: response.data.estimatedDuration,
            tags: response.data.tags,
            visibility: response.data.visibility,
            moduleId: response.data.moduleId,
            courseId: response.data.courseId,
            author: response.data.author || "Unknown",
            status:
              response.data.status === "deleted"
                ? "archived"
                : (response.data.status as ContentItem["status"]),
            createdAt:
              typeof response.data.createdAt === "string"
                ? response.data.createdAt
                : new Date(response.data.createdAt).toISOString(),
            updatedAt:
              typeof response.data.updatedAt === "string"
                ? response.data.updatedAt
                : new Date(response.data.updatedAt).toISOString(),
          };
          setContents((prev) => [newContent, ...prev]);
        } else {
          setError("Failed to create content");
        }
      }

      setShowForm(false);
      setEditingContent(undefined);
    } catch (error) {
      console.error("Error saving content:", error);
      setError("Failed to save content. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteContent = async (contentId: string) => {
    try {
      setLoading(true);
      const response = await contentService.deleteContent(
        contentId,
        false,
        courseId
      );

      if (response.success) {
        setContents((prev) => prev.filter((c) => c._id !== contentId));
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
      } else {
        setError("Failed to delete content");
      }
    } catch (error) {
      console.error("Error deleting content:", error);
      setError("Failed to delete content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);

      // Delete all selected items
      const deletePromises = selectedItems.map((id) =>
        contentService.deleteContent(id, false, courseId)
      );

      const results = await Promise.allSettled(deletePromises);

      // Check if any deletions failed
      const failedDeletions = results.filter(
        (result) =>
          result.status === "rejected" ||
          (result.status === "fulfilled" && !result.value.success)
      ).length;

      if (failedDeletions > 0) {
        setError(`Failed to delete ${failedDeletions} item(s)`);
      }

      // Remove successfully deleted items from the list
      const successfullyDeleted = selectedItems.filter((_, index) => {
        const result = results[index];
        return result.status === "fulfilled" && result.value.success;
      });

      setContents((prev) =>
        prev.filter((c) => !successfullyDeleted.includes(c._id))
      );
      setSelectedItems([]);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error bulk deleting contents:", error);
      setError("Failed to delete selected contents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleContentStatus = async (contentId: string) => {
    try {
      // TODO: Replace with actual API call
      setContents((prev) =>
        prev.map((c) =>
          c._id === contentId
            ? {
                ...c,
                status:
                  c.status === "published" ? "draft" : ("published" as any),
              }
            : c
        )
      );
    } catch (error) {
      console.error("Error toggling content status:", error);
    }
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="w-5 h-5" />;
      case "document":
        return <FileText className="w-5 h-5" />;
      case "image":
        return <Image className="w-5 h-5" />;
      case "link":
        return <LinkIcon className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published":
        return "bg-green-100 text-green-800";
      case "draft":
        return "bg-yellow-100 text-yellow-800";
      case "archived":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const filteredContents = contents.filter((content) => {
    return (
      (!filters.search ||
        content.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        content.description
          .toLowerCase()
          .includes(filters.search.toLowerCase())) &&
      (!filters.type || content.type === filters.type) &&
      (!filters.status || content.status === filters.status) &&
      (!filters.author ||
        content.author.toLowerCase().includes(filters.author.toLowerCase()))
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="w-16 h-16 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Content
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Content Preview Modal Component
  const ContentPreviewModal: React.FC<{
    content: ContentItem | null;
    onClose: () => void;
    isOpen: boolean;
  }> = ({ content, onClose, isOpen }) => {
    if (!isOpen || !content) return null;

    const renderContentPreview = () => {
      switch (content.type) {
        case "video":
          return content.mediaUrl ? (
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <video controls className="w-full h-full" src={content.mediaUrl}>
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Video className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No video URL provided</p>
              </div>
            </div>
          );

        case "image":
          return content.mediaUrl ? (
            <div className="rounded-lg overflow-hidden">
              <img
                src={content.mediaUrl}
                alt={content.title}
                className="w-full max-h-96 object-contain bg-gray-100"
              />
            </div>
          ) : (
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No image URL provided</p>
              </div>
            </div>
          );

        case "link":
          return (
            <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
              <div className="flex items-center space-x-3 mb-4">
                <LinkIcon className="w-6 h-6 text-blue-500" />
                <span className="font-medium text-gray-900">External Link</span>
              </div>
              {content.externalUrl ? (
                <a
                  href={content.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline break-all"
                >
                  {content.externalUrl}
                </a>
              ) : (
                <p className="text-gray-500">No URL provided</p>
              )}
            </div>
          );

        case "document":
        case "text":
        default:
          return (
            <div className="prose max-w-none">
              {content.content ? (
                <div
                  className="border border-gray-200 rounded-lg p-6 bg-white"
                  dangerouslySetInnerHTML={{ __html: content.content }}
                />
              ) : (
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No content available</p>
                </div>
              )}
            </div>
          );
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {content.title}
                </h2>
                <div className="flex items-center space-x-4 mt-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      content.status
                    )}`}
                  >
                    {content.status}
                  </span>
                  <div className="flex items-center text-sm text-gray-500">
                    {getContentIcon(content.type)}
                    <span className="ml-1 capitalize">{content.type}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{content.estimatedDuration} min</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Description */}
            {content.description && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Description
                </h3>
                <p className="text-gray-600">{content.description}</p>
              </div>
            )}

            {/* Content Preview */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Content
              </h3>
              {renderContentPreview()}
            </div>

            {/* Tags */}
            {content.tags && content.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {content.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Author:</span>
                  <span className="ml-2 text-gray-900">{content.author}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Visibility:</span>
                  <span className="ml-2 text-gray-900 capitalize">
                    {content.visibility}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Created:</span>
                  <span className="ml-2 text-gray-900">
                    {new Date(content.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Updated:</span>
                  <span className="ml-2 text-gray-900">
                    {new Date(content.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="flex justify-between">
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setEditingContent(content);
                      setShowForm(true);
                      onClose();
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Edit Content</span>
                  </button>
                  <button
                    onClick={() => toggleContentStatus(content._id)}
                    className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {content.status === "published" ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        <span>Unpublish</span>
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        <span>Publish</span>
                      </>
                    )}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setDeleteTarget(content._id);
                    setShowDeleteConfirm(true);
                    onClose();
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Content Card Component
  const ContentCard: React.FC<{ content: ContentItem }> = ({ content }) => {
    const renderContentThumbnail = () => {
      switch (content.type) {
        case "video":
          return content.mediaUrl ? (
            <div className="aspect-video bg-black rounded-t-lg overflow-hidden">
              <video
                className="w-full h-full object-cover"
                src={content.mediaUrl}
                preload="metadata"
              >
                <div className="flex items-center justify-center h-full">
                  <Video className="w-12 h-12 text-white opacity-75" />
                </div>
              </video>
            </div>
          ) : (
            <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
              <Video className="w-12 h-12 text-gray-400" />
            </div>
          );

        case "image":
          return content.mediaUrl ? (
            <div className="aspect-video bg-gray-200 rounded-t-lg overflow-hidden">
              <img
                src={content.mediaUrl}
                alt={content.title}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
              <Image className="w-12 h-12 text-gray-400" />
            </div>
          );

        case "link":
          return (
            <div className="aspect-video bg-gradient-to-br from-blue-500 to-purple-600 rounded-t-lg flex items-center justify-center">
              <LinkIcon className="w-12 h-12 text-white" />
            </div>
          );

        case "document":
          return (
            <div className="aspect-video bg-gradient-to-br from-green-500 to-teal-600 rounded-t-lg flex items-center justify-center">
              <FileText className="w-12 h-12 text-white" />
            </div>
          );

        case "text":
        default:
          return (
            <div className="aspect-video bg-gradient-to-br from-gray-500 to-gray-700 rounded-t-lg flex items-center justify-center relative">
              <BookOpen className="w-12 h-12 text-white" />
              {content.content && (
                <div className="absolute inset-0 bg-black/50 rounded-t-lg p-4 overflow-hidden">
                  <div
                    className="text-white text-xs line-clamp-6"
                    dangerouslySetInnerHTML={{
                      __html:
                        content.content.substring(0, 150) +
                        (content.content.length > 150 ? "..." : ""),
                    }}
                  />
                </div>
              )}
            </div>
          );
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
        {/* Thumbnail */}
        {renderContentThumbnail()}

        {/* Content Info */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-medium text-gray-900 line-clamp-2">
              {content.title}
            </h3>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                content.status
              )}`}
            >
              {content.status}
            </span>
          </div>

          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {content.description}
          </p>

          {/* Metadata */}
          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                {getContentIcon(content.type)}
                <span className="ml-1 capitalize">{content.type}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                <span>{content.estimatedDuration} min</span>
              </div>
            </div>
            <div className="flex items-center">
              <User className="w-3 h-3 mr-1" />
              <span>{content.author}</span>
            </div>
          </div>

          {/* Tags */}
          {content.tags && content.tags.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-1">
                {content.tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
                  >
                    {tag}
                  </span>
                ))}
                {content.tags.length > 3 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                    +{content.tags.length - 3}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setPreviewContent(content);
                  setShowPreview(true);
                }}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                <Eye className="w-3 h-3" />
                <span>View</span>
              </button>
              <button
                onClick={() => {
                  setEditingContent(content);
                  setShowForm(true);
                }}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                <Edit3 className="w-3 h-3" />
                <span>Edit</span>
              </button>
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => toggleContentStatus(content._id)}
                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                title={content.status === "published" ? "Unpublish" : "Publish"}
              >
                {content.status === "published" ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => {
                  setDeleteTarget(content._id);
                  setShowDeleteConfirm(true);
                }}
                className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Content Management
              </h1>
              <p className="text-gray-600">
                Manage course content, materials, and resources
              </p>
            </div>

            <button
              onClick={() => {
                setEditingContent(undefined);
                setShowForm(true);
              }}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Add Content</span>
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search content..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("cards")}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    viewMode === "cards"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Cards
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    viewMode === "table"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Table
                </button>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
                {showFilters ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {selectedItems.length > 0 && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete ({selectedItems.length})</span>
                </button>
              )}
            </div>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Type
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, type: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Types</option>
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                    <option value="image">Image</option>
                    <option value="link">Link</option>
                    <option value="text">Text</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by author..."
                    value={filters.author}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        author: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() =>
                      setFilters({
                        search: "",
                        type: "",
                        status: "",
                        author: "",
                        moduleId: "",
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredContents.length} of {contents.length} content items
          </p>
        </div>

        {/* Content List */}
        {filteredContents.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No content found
            </h3>
            <p className="text-gray-600 mb-4">
              {contents.length === 0
                ? "Start by creating your first content item"
                : "Try adjusting your filters or search terms"}
            </p>
            <button
              onClick={() => {
                setEditingContent(undefined);
                setShowForm(true);
              }}
              className="text-blue-600 hover:text-blue-700"
            >
              Create Content
            </button>
          </div>
        ) : viewMode === "cards" ? (
          /* Card View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredContents.map((content) => (
              <ContentCard key={content._id} content={content} />
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div ref={tableScrollRef} className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={
                          selectedItems.length === filteredContents.length &&
                          filteredContents.length > 0
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(
                              filteredContents.map((c) => c._id)
                            );
                          } else {
                            setSelectedItems([]);
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Content
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Author
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredContents.map((content) => (
                    <tr key={content._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(content._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems((prev) => [
                                ...prev,
                                content._id,
                              ]);
                            } else {
                              setSelectedItems((prev) =>
                                prev.filter((id) => id !== content._id)
                              );
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="text-blue-600 mr-3">
                            {getContentIcon(content.type)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {content.title}
                            </div>
                            <div className="text-sm text-gray-500 max-w-xs truncate">
                              {content.description}
                            </div>
                            {content.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {content.tags.slice(0, 3).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                  >
                                    <Tag className="w-3 h-3 mr-1" />
                                    {tag}
                                  </span>
                                ))}
                                {content.tags.length > 3 && (
                                  <span className="text-xs text-gray-500">
                                    +{content.tags.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {content.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                            content.status
                          )}`}
                        >
                          {content.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {content.author}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {content.estimatedDuration} min
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {new Date(content.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setPreviewContent(content);
                              setShowPreview(true);
                            }}
                            className="text-gray-400 hover:text-green-600"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => toggleContentStatus(content._id)}
                            className="text-gray-400 hover:text-blue-600"
                            title={
                              content.status === "published"
                                ? "Unpublish"
                                : "Publish"
                            }
                          >
                            {content.status === "published" ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>

                          <button
                            onClick={() => {
                              setEditingContent(content);
                              setShowForm(true);
                            }}
                            className="text-gray-400 hover:text-blue-600"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => {
                              setDeleteTarget(content._id);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Content Preview Modal */}
      <ContentPreviewModal
        content={previewContent}
        onClose={() => {
          setShowPreview(false);
          setPreviewContent(null);
        }}
        isOpen={showPreview}
      />

      {/* Content Form Modal */}
      <ContentForm
        content={editingContent}
        onSave={handleSaveContent}
        onCancel={() => {
          setShowForm(false);
          setEditingContent(undefined);
        }}
        isOpen={showForm}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>

            <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
              Confirm Deletion
            </h3>

            <p className="text-sm text-gray-500 text-center mb-6">
              {deleteTarget
                ? "Are you sure you want to delete this content? This action cannot be undone."
                : `Are you sure you want to delete ${selectedItems.length} content items? This action cannot be undone.`}
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteTarget) {
                    handleDeleteContent(deleteTarget);
                  } else {
                    handleBulkDelete();
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
